import React, { useCallback, useEffect, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { useVirtualizedScrollspy } from '@garnish/layout';
import { type FlashListRef } from '@shopify/flash-list';

import {
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationHelpers,
} from '@sg/shared-fe-features-customization';

import { useCustomizationIngredientRows } from '../../hooks';
import {
  type CustomizationIngredientRow,
  getCustomizationIngredientRowTargetId,
} from '../../utils';
import { CustomizationIngredientsList } from '../CustomizationIngredientsList';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridPanel = (props: Props) => {
  const { actor, activeTargetId, modificationGroups } = props;

  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  const { getFormattedQuantity } = useCustomizationHelpers({ actor });

  const { rows, itemsPerRow } = useCustomizationIngredientRows({ actor, modificationGroups });

  // ─── Refs ────────────────────────────────────────────────────────────

  const listRef = useRef<FlashListRef<CustomizationIngredientRow>>(null);

  // ─── Scrollspy ───────────────────────────────────────────────────────

  const { initialScrollIndex, scrollTo, onViewableItemsChanged } = useVirtualizedScrollspy({
    listRef,
    rows,
    getTargetId: getCustomizationIngredientRowTargetId,
    defaultTargetId: activeTargetId ?? undefined,
  });

  // ─── Handlers ────────────────────────────────────────────────────────

  const getSectionSubheading = useCallback(
    (group: CustomizationMachineTypes.ExtendedModifierGroup) => getFormattedQuantity(group.name),
    [getFormattedQuantity],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!activeTargetId) return;

    scrollTo(activeTargetId);
  }, [activeTargetId, scrollTo]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentContainerDynamicStyles = useStyle(() => {
    const bottomInset = Platform.select({ android: safeAreaBottomInset, default: 0 });

    return {
      paddingBottom: theme.spacing['30'] + bottomInset,
    };
  }, [safeAreaBottomInset]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.ingredientsGridContainer}>
      <CustomizationIngredientsList
        ref={listRef}
        rows={rows}
        itemsPerRow={itemsPerRow}
        actor={actor}
        initialScrollIndex={initialScrollIndex}
        onViewableItemsChanged={onViewableItemsChanged}
        getSectionHelperText={getSectionHelperText}
        getSectionSubheading={getSectionSubheading}
        contentContainerStyle={contentContainerDynamicStyles}
      />
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSectionHelperText(group: CustomizationMachineTypes.ExtendedModifierGroup) {
  return group.helperText;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientsGridContainer: {
    flex: 1,
    backgroundColor: theme.colors.OATMEAL,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  activeTargetId: string | null | undefined;
  modificationGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
};
